/**
 * `Lift: LiftApi` (W1-04 core-ir; contract core/ir/types.ts, DESIGN.md §6.5, §7.2).
 *
 * The exact inverse of lowering: normalised RPE IR (+ extra.json IR) -> a new document. Provenance is captured on
 * every lift (KeySrc, NoteSrc, raw triples, explicit-null keys, slot styles, passthrough), so an unedited round trip
 * writes the same chart again (L1). Ids come from one counter in a fixed order: BPM points, groups, markers, lines,
 * then per line its tracks / keys and notes, then effects (with their variable tracks) and videos.
 *
 * Chart-level fields:
 *  - META: name, level, charter, composer, illustration (the illustrator's name) -> meta; id -> rpeId; offset ->
 *    timing.offsetMs; RPEVersion -> pkg.rpeVersion (effective) + pkg.rpeVersionRaw (raw lexeme); unknown keys ->
 *    meta.metaExtra. META.song / background are file names Phira uses only to infer a package without info files:
 *    the package importer resolves music and illustration from the manifest (they are not lifted here);
 *  - BPMList -> tempo points (raw triples kept; of several points at one beat the last, as Phira uses it,
 *    reported 'normalized'); judgeLineGroup -> groups with `order` = position (the inverse of
 *    lowerCtx.ts#groupLayout): "Default" at position 0 is RPE's group of ungrouped lines and is not modelled
 *    (`Group` 0 -> no group), an unused "" entry below the last position is a gap Lower writes again and is not
 *    modelled, every other entry is a group; timeTags -> markers; multiLineString / multiScale / chartTime /
 *    xybind / unknown root keys -> rpeRoot; extra.json root keys -> pkg.extraJsonRoot (non-null whenever an
 *    extra.json was imported);
 *  - parent cycles left in the IR (never after normalisation; a line that is its own parent included) are broken
 *    and reported: each cycle loses one parent link, that of its member listed first; lines outside it keep theirs.
 * The returned document references assets through `LiftContext.assetIdForPath`; the package importer adds the
 * asset records (it hashed the files). pkg uses the editor defaults except the speed mode and RPEVersion fields;
 * the importer applies info.yml on top.
 */

import { Defaults, FIRST_ID } from '../model/defaults';
import type {
  BpmId,
  BpmPoint,
  ChartDoc,
  GroupId,
  Line,
  LineGroup,
  LineId,
  LineProp,
  Marker,
  MarkerId,
  RawNumberLexeme,
  SpeedMode,
} from '../model/types';
import { Beats } from '../time/beat';
import type { Triple } from '../time/types';
import type { Passthrough, RGB } from '../util/types';
import { liftExtraJson } from './liftExtra';
import { DEFAULT_GROUP_NAME, GROUP_GAP_NAME } from './lowerCtx';
import { liftLine, LiftReport, type LiftEnv } from './liftLine';
import { liftedKeys, liftLineEvents } from './liftTrack';
import { tripleIn } from './triples';
import type {
  ImportReportEntry,
  IrChart,
  IrEvent,
  IrExtraJson,
  LiftApi,
  LiftContext,
  LiftedKey,
  LiftResult,
} from './types';

function isLexeme(v: number | RawNumberLexeme): v is RawNumberLexeme {
  return typeof v === 'object';
}

/** Walk states of `breakCycles`. */
const UNVISITED = 0;
const ON_WALK = 1;
const DONE = 2;

/**
 * Breaks parent cycles: every cycle loses exactly one parent link, that of its member listed first in
 * judgeLineList, and only cycle members are touched (a line whose ancestor chain merely runs INTO a cycle keeps
 * its parent). Each line has one parent, so a walk up from an unvisited line ends at a root, at a line an earlier
 * walk finished, or back at a line of the same walk, which closes exactly one new cycle. O(lines).
 */
function breakCycles(lines: Line[], env: LiftEnv): void {
  const indexOf = new Map(lines.map((l, i) => [l.id, i]));
  const state = new Uint8Array(lines.length);
  const parentIndex = (i: number): number | undefined => {
    const parent = lines[i]?.parent ?? null;
    return parent === null ? undefined : indexOf.get(parent);
  };
  for (let start = 0; start < lines.length; start++) {
    if (state[start] !== UNVISITED) continue;
    const walk: number[] = [];
    let cur: number | undefined = start;
    while (cur !== undefined && state[cur] === UNVISITED) {
      state[cur] = ON_WALK;
      walk.push(cur);
      cur = parentIndex(cur);
    }
    if (cur !== undefined && state[cur] === ON_WALK) {
      // walk[from..] is the cycle (cur -> ... -> back to cur).
      const at = walk.slice(walk.indexOf(cur)).reduce((a, b) => Math.min(a, b));
      const line = lines[at];
      if (line !== undefined) {
        lines[at] = { ...line, parent: null };
        env.report('changed', 'ir.lift.fatherCycle', { line: at }, `judgeLineList[${String(at)}].father`, at);
      }
    }
    for (const i of walk) state[i] = DONE;
  }
}

/**
 * Groups of judgeLineGroup (`order` = position), filling `groups`; returns the group of every position (null:
 * position 0 "Default", RPE's group of lines without a group, or an unused gap entry Lower writes again). An empty
 * list behaves as ["Default"].
 */
function liftGroups(chart: IrChart, env: LiftEnv, groups: Map<GroupId, LineGroup>): (GroupId | null)[] {
  const names = chart.judgeLineGroup;
  if (names.length === 0) return [null];
  const used = new Set(chart.judgeLineList.map((l) => l.Group));
  return names.map((name, order) => {
    if (order === 0 && name === DEFAULT_GROUP_NAME) return null;
    if (name === GROUP_GAP_NAME && order < names.length - 1 && !used.has(order)) return null;
    const id = env.nextId() as GroupId;
    groups.set(id, { id, name, order, color: null });
    return id;
  });
}

function liftChart(chart: IrChart, extra: IrExtraJson | null, ctx: LiftContext): LiftResult {
  let seq = FIRST_ID;
  const report = new LiftReport();
  const env: LiftEnv = {
    speedMode: ctx.speedMode,
    assetIdForPath: ctx.assetIdForPath,
    nextId: () => seq++,
    report: (cls, key, params, path, line) => {
      report.add(cls, key, params, path, line);
    },
    tracks: new Map(),
    keys: new Map(),
    notes: new Map(),
  };

  // Tempo. Of several points at one beat only the LAST takes effect in Phira (its walk adds no time between them and
  // looks up the last point at or before a beat, F§3.2), so the earlier ones are dropped, render-identically. The
  // RPE and PEC importers already remove them (CR-W3-02b-4); the entry reuses the RPE importer's text for it.
  const bpm = new Map<BpmId, BpmPoint>();
  const lastAtBeat = new Map<string, number>();
  const beatsIn = chart.BPMList.map((p, i) => {
    const t = tripleIn(p.startTime);
    if (t !== null) lastAtBeat.set(Beats.key(t.beat), i);
    return t;
  });
  chart.BPMList.forEach((p, i) => {
    const t = beatsIn[i] ?? null;
    if (t === null) {
      env.report('changed', 'ir.lift.eventSkipped', undefined, `BPMList[${String(i)}]`);
      return;
    }
    if (lastAtBeat.get(Beats.key(t.beat)) !== i) {
      env.report('normalized', 'rpe.report.bpmDuplicate', undefined, `BPMList[${String(i)}]`);
      return;
    }
    const id = env.nextId() as BpmId;
    const src: { rawStart?: Triple; extra?: Passthrough } = {};
    if (t.raw !== undefined) src.rawStart = t.raw;
    if (p.extra.length > 0) src.extra = p.extra;
    bpm.set(id, { id, t: t.beat, bpm: p.bpm, ...(Object.keys(src).length > 0 ? { src } : {}) });
  });

  // Groups.
  const groups = new Map<GroupId, LineGroup>();
  const groupIds = liftGroups(chart, env, groups);

  // Markers.
  const markers = new Map<MarkerId, Marker>();
  (chart.timeTags ?? []).forEach((tag, i) => {
    const t = tripleIn(tag.time);
    if (t === null) {
      env.report('changed', 'ir.lift.eventSkipped', undefined, `timeTags[${String(i)}]`);
      return;
    }
    const id = env.nextId() as MarkerId;
    markers.set(id, { id, t: t.beat, name: tag.name, color: null });
  });

  // Lines.
  const lineIds = chart.judgeLineList.map(() => env.nextId() as LineId);
  const lineList = chart.judgeLineList.map((ir, li) =>
    liftLine(env, ir, li, lineIds[li] as LineId, { lineIds, groupIds }),
  );
  breakCycles(lineList, env);

  // extra.json.
  const out = extra === null ? { effects: [], videos: [] } : liftExtraJson(env, extra, chart.BPMList, lineIds);

  const meta = chart.META;
  const doc: ChartDoc = {
    kind: 'phiedit-doc',
    version: 1,
    idSeq: seq,
    meta: {
      ...Defaults.meta(),
      name: meta.name,
      composer: meta.composer,
      illustrator: meta.illustration,
      charter: meta.charter,
      level: meta.level,
      rpeId: meta.id,
      metaExtra: meta.extra,
    },
    pkg: {
      ...Defaults.pkg(),
      speedMode: ctx.speedMode,
      rpeVersion: meta.rpeVersionEffective,
      rpeVersionRaw: isLexeme(meta.RPEVersion) ? meta.RPEVersion : null,
      extraJsonRoot: extra === null ? null : extra.rootExtra,
    },
    timing: { ...Defaults.timing(), offsetMs: meta.offset, confirmed: true },
    editor: Defaults.editorData(),
    rpeRoot: {
      multiLineString: chart.multiLineString ?? null,
      multiScale: chart.multiScale ?? null,
      chartTime: chart.chartTime ?? null,
      xybind: chart.xybind ?? null,
      extra: chart.rootExtra,
    },
    bpm,
    lines: new Map(lineList.map((l) => [l.id, l])),
    lineOrder: lineIds,
    tracks: env.tracks,
    keys: env.keys,
    notes: env.notes,
    effects: new Map(out.effects.map((e) => [e.id, e])),
    effectOrder: out.effects.map((e) => e.id),
    videos: new Map(out.videos.map((v) => [v.id, v])),
    markers,
    groups,
    generators: new Map(),
    assets: new Map(),
  };
  return { doc, report: report.list() };
}

/** Result of `liftEventsChecked`. */
export interface LiftEventsResult {
  /** The keys, exactly as `Lift.liftEvents` returns them for the same list. */
  readonly keys: readonly LiftedKey[];
  /**
   * Indices (into the given list) of the events that could not be lifted and are not in `keys`: an invalid beat
   * triple, a negative length, or a start before the previous event's start (unsorted / overlapping lists).
   */
  readonly skipped: readonly number[];
  /** One `ir.lift.eventSkipped` entry (class 'changed') per skipped event, path `[i]` (prefix the list's own path). */
  readonly report: readonly ImportReportEntry[];
}

/**
 * `Lift.liftEvents` plus what it left out (additive export for the Raw events panel and RPE clipboard snippets).
 * `liftEvents` returns only the keys, so an edited or pasted list with an unliftable event silently loses it;
 * callers that take user-written lists use this and show `skipped` / `report` instead (or refuse the edit). Events
 * Lower itself wrote as padding (origin 'padding') are dropped on purpose and are not reported.
 */
export function liftEventsChecked(
  events: readonly IrEvent<number | RGB | string>[],
  prop: LineProp,
  speedMode: SpeedMode,
): LiftEventsResult {
  const { keys, skipped } = liftLineEvents(events, prop, speedMode);
  return {
    keys: keys as LiftedKey[],
    skipped,
    report: skipped.map((i) => ({
      cls: 'changed',
      message: { key: 'ir.lift.eventSkipped' },
      path: `[${String(i)}]`,
    })),
  };
}

/**
 * Note on `liftEvents`: events that cannot be lifted (see `LiftEventsResult.skipped`) are left out of the returned
 * keys without a report, as the contract signature returns keys only; use `liftEventsChecked` to learn about them.
 */
export const Lift: LiftApi = {
  liftChart,
  liftEvents: liftedKeys,
};
